/**
 * MorningBriefCard — a clean, single-card Morning Brief shown at the top of the
 * Recommendations feed. Shows the latest sweep with an inline sensitivity
 * selector, a scheduling popup, a "Run sweep now" action, and an
 * "Add to dashboard" action (adds the brief as a text tile to any dashboard,
 * mirroring the AI Explore save-to-dashboard flow).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sunrise, Loader2, RefreshCw, CalendarClock, LayoutDashboard, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { api } from '@/lib/api';
import { SlackChannelPicker, TeamsChannelPicker } from '@/components/ChannelPickers';

const SENSITIVITY = [
  { key: 'conservative', label: 'Conservative (3σ)' },
  { key: 'balanced', label: 'Balanced (2σ)' },
  { key: 'aggressive', label: 'Aggressive (1.5σ)' },
];
const inputCls = 'w-full px-3 py-2 rounded-lg bg-muted/40 border border-border/50 text-sm text-foreground outline-none focus:border-primary/50';

function fmtPct(n) { return typeof n === 'number' ? `${n >= 0 ? '+' : ''}${n.toFixed(2)}%` : ''; }

export default function MorningBriefCard({ isAdmin }) {
  const navigate = useNavigate();
  const [brief, setBrief] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [schedOpen, setSchedOpen] = useState(false);
  const [dashOpen, setDashOpen] = useState(false);
  const [emailsText, setEmailsText] = useState('');
  const [slackChannels, setSlackChannels] = useState([]);
  const [teamsChannels, setTeamsChannels] = useState([]);
  const [savingCfg, setSavingCfg] = useState(false);
  const [holidaysText, setHolidaysText] = useState('');
  const [dashboards, setDashboards] = useState([]);
  const [addSaving, setAddSaving] = useState(false);
  const pollRef = useRef(null);

  const loadLatest = useCallback(async () => {
    const list = await api.getMorningBriefs().catch(() => ({ items: [] }));
    const latest = (list.items || [])[0];
    if (!latest) { setBrief(null); return null; }
    if (latest.status === 'completed') {
      const full = await api.getMorningBrief(latest.id).catch(() => latest);
      setBrief(full);
    } else {
      setBrief(latest);
    }
    return latest;
  }, []);

  const load = useCallback(async () => {
    try {
      const [c] = await Promise.all([api.getMorningBriefConfig().catch(() => null), loadLatest()]);
      if (c) {
        setCfg(c);
        setEmailsText((c.notify_emails || []).join(', '));
        setSlackChannels(c.notify_slack_channels || []);
        setTeamsChannels(c.notify_teams_channels || []);
        setHolidaysText((c.holidays || []).join('\n'));
      }
    } finally { setLoading(false); }
  }, [loadLatest]);

  useEffect(() => { load(); return () => { if (pollRef.current) clearInterval(pollRef.current); }; }, [load]);

  useEffect(() => {
    if (brief?.status === 'running' && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        const latest = await loadLatest();
        if (!latest || latest.status !== 'running') { clearInterval(pollRef.current); pollRef.current = null; setBusy(false); }
      }, 3000);
    }
  }, [brief, loadLatest]);

  const runSweep = async (sensitivity) => {
    if (!isAdmin) return;
    setBusy(true);
    try {
      if (sensitivity && cfg) {
        const next = { ...cfg, sensitivity };
        await api.updateMorningBriefConfig(next);
        setCfg(next);
      }
      await api.generateMorningBrief();
      toast.success('Running the morning sweep…');
      await loadLatest();
    } catch (e) { toast.error(e.message || 'Could not start sweep'); setBusy(false); }
  };

  const saveSchedule = async () => {
    setSavingCfg(true);
    try {
      const payload = {
        ...cfg,
        notify_emails: emailsText.split(/[,\n]/).map(s => s.trim()).filter(Boolean),
        notify_slack_channels: slackChannels,
        notify_teams_channels: teamsChannels,
        holidays: holidaysText.split(/[,\n]/).map(s => s.trim()).filter(Boolean),
      };
      const res = await api.updateMorningBriefConfig(payload);
      setCfg(res);
      toast.success('Schedule saved');
      setSchedOpen(false);
    } catch (e) { toast.error(e.message || 'Could not save schedule'); }
    finally { setSavingCfg(false); }
  };

  const patchCfg = (patch) => setCfg(c => ({ ...(c || {}), ...patch }));
  const patchChannel = (k, v) => setCfg(c => ({ ...c, channels: { ...(c?.channels || {}), [k]: v } }));

  // Drill in: open this metric in AI Explore with a ready-made question.
  const drillIn = (chip) => {
    if (!chip?.model_id) { toast.error('This metric can\'t be drilled into'); return; }
    sessionStorage.setItem('exploreAutoAsk', JSON.stringify({
      model_id: chip.model_id,
      question: `Show me ${chip.label} over time`,
    }));
    navigate('/ai-copilot');
  };

  const briefText = () => {
    if (!brief) return '';
    const lines = [brief.headline || 'Morning Brief'];
    (brief.bullets || []).forEach(b => lines.push(`• ${b}`));
    return lines.join('\n');
  };

  const openDash = async () => {
    if (!brief || brief.status !== 'completed') { toast.error('No completed brief to add yet'); return; }
    setDashOpen(true);
    try {
      const reports = await api.getReports();
      setDashboards((reports || []).sort((a, b) => new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0)));
    } catch { setDashboards([]); }
  };

  const makeVisual = () => ({
    id: 'v_' + Date.now(), vizType: 'rich_text', title: 'Morning Brief',
    textContent: briefText(), rowDims: [], colDim: '', valueMeasures: [], filterDims: [], _reportPageIndex: 0,
  });

  const addToExisting = async (reportId) => {
    setAddSaving(reportId);
    try {
      const rep = await api.getReport(reportId);
      const cfgR = rep.config || {};
      const nv = makeVisual();
      const visuals = [...(Array.isArray(cfgR.visuals) ? cfgR.visuals : []), nv];
      let pageVisuals = cfgR.pageVisuals;
      if (pageVisuals && typeof pageVisuals === 'object' && !Array.isArray(pageVisuals)) {
        const firstPage = (cfgR.pages && cfgR.pages[0]?.id) || Object.keys(pageVisuals)[0] || 'page_1';
        pageVisuals = { ...pageVisuals, [firstPage]: [...(pageVisuals[firstPage] || []), nv] };
      }
      let gridLayouts = cfgR.gridLayouts;
      if (gridLayouts && Array.isArray(gridLayouts.lg)) {
        const maxY = gridLayouts.lg.reduce((m, l) => Math.max(m, (l.y || 0) + (l.h || 4)), 0);
        gridLayouts = { ...gridLayouts, lg: [...gridLayouts.lg, { i: nv.id, x: 0, y: maxY, w: 12, h: 4, minW: 2, minH: 1 }] };
      }
      await api.updateReport(reportId, { config: { ...cfgR, visuals, ...(pageVisuals ? { pageVisuals } : {}), ...(gridLayouts ? { gridLayouts } : {}) } });
      toast.success(`Added to "${rep.name}"`);
      setDashOpen(false);
    } catch (e) { toast.error(e.message || 'Could not add to dashboard'); }
    finally { setAddSaving(false); }
  };

  const addToNew = async () => {
    setAddSaving('new');
    try {
      const nv = makeVisual();
      const r = await api.createReport({ name: `Morning Brief — ${brief.brief_date || ''}`.trim(), description: brief.headline || '', visualization_type: 'rich_text' });
      await api.updateReport(r.report_id, {
        name: `Morning Brief — ${brief.brief_date || ''}`.trim(),
        config: { visuals: [nv] },
      });
      toast.success('New dashboard created');
      setDashOpen(false);
    } catch (e) { toast.error(e.message || 'Could not create dashboard'); }
    finally { setAddSaving(false); }
  };

  if (loading) {
    return <div className="rounded-xl border bg-card p-4 mb-4 flex items-center gap-2 text-sm text-muted-foreground" data-testid="morning-brief-card"><Loader2 className="w-4 h-4 animate-spin" /> Loading Morning Brief…</div>;
  }

  const tone = brief?.tone || 'calm';
  const toneCls = tone === 'attention' ? 'bg-amber-500/15 text-amber-500 border-amber-500/30' : 'bg-sky-500/15 text-sky-500 border-sky-500/30';
  const chips = [];
  (brief?.anomalies || []).forEach(a => chips.push({ label: a.label, pct: a.pct_change, model_id: a.model_id, measure: a.measure }));
  (brief?.movers_up || []).concat(brief?.movers_down || []).forEach(m => {
    if (!chips.find(c => c.label === m.label)) chips.push({ label: m.label, pct: m.pct_change, model_id: m.model_id, measure: m.measure });
  });
  const sensitivity = cfg?.sensitivity || 'balanced';

  return (
    <div className="rounded-xl border bg-card p-4 mb-4" data-testid="morning-brief-card">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Sunrise className="w-4 h-4 text-amber-500" />
          <span className="text-xs font-bold tracking-wide text-foreground">MORNING BRIEF</span>
          {brief && <span className={`text-[10px] font-semibold px-2 py-0.5 rounded border uppercase ${toneCls}`}>{tone}</span>}
        </div>
        <div className="flex-1 flex justify-center">
          {brief?.status === 'completed' && (
            <Button variant="outline" size="sm" onClick={openDash} className="rounded-lg text-xs h-7 gap-1.5 text-primary border-primary/30" data-testid="mb-add-to-dashboard">
              <LayoutDashboard className="w-3.5 h-3.5" /> Add to dashboard
            </Button>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={() => setSchedOpen(true)} className="rounded-lg text-xs h-7 gap-1.5" data-testid="mb-schedule-btn" title="Scheduling & delivery">
              <CalendarClock className="w-3.5 h-3.5" /> Schedule
            </Button>
          )}
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={() => runSweep()} disabled={busy} className="rounded-lg text-xs h-7 gap-1.5" data-testid="mb-run-sweep">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Run sweep now
            </Button>
          )}
        </div>
      </div>

      {/* Sensitivity */}
      <div className="flex items-center gap-2 flex-wrap mt-3">
        <span className="text-xs text-muted-foreground">Sensitivity:</span>
        {SENSITIVITY.map(s => (
          <button
            key={s.key}
            onClick={() => isAdmin && s.key !== sensitivity && runSweep(s.key)}
            disabled={!isAdmin || busy}
            className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${s.key === sensitivity ? 'bg-primary/15 text-primary border-primary/40 font-medium' : 'bg-muted/30 text-muted-foreground border-border/40 hover:border-border'} ${!isAdmin ? 'cursor-default' : ''}`}
            data-testid={`mb-sensitivity-${s.key}`}
          >
            {s.label}
          </button>
        ))}
        <span className="text-[11px] text-muted-foreground/70">Auto-armed sentinels use each metric's own history — not the single observed spike.</span>
      </div>

      {/* Body */}
      {!brief ? (
        <div className="mt-4 text-sm text-muted-foreground">
          No morning brief yet.{isAdmin ? ' Run a sweep to generate one.' : ''}
        </div>
      ) : brief.status === 'running' ? (
        <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Running the morning sweep…</div>
      ) : brief.status === 'failed' ? (
        <div className="mt-4 text-sm text-destructive">Sweep failed: {brief.error || 'unknown error'}</div>
      ) : (
        <>
          <h3 className="text-base font-semibold text-foreground mt-3" data-testid="mb-headline">{brief.headline}</h3>
          <ul className="mt-1.5 space-y-1">
            {(brief.bullets || []).map((b, i) => (
              <li key={i} className="text-sm text-muted-foreground flex gap-1.5"><span className="text-amber-500">•</span>{b}</li>
            ))}
          </ul>
          {chips.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {chips.slice(0, 8).map((c, i) => (
                <button
                  key={i}
                  onClick={() => drillIn(c)}
                  title={`Open "${c.label}" in AI Explore`}
                  className="px-2 py-0.5 rounded-md text-[11px] bg-muted/40 border border-border/40 text-foreground/80 hover:bg-primary/10 hover:border-primary/40 hover:text-primary transition-colors cursor-pointer"
                  data-testid={`mb-chip-${i}`}
                >
                  {c.label} {fmtPct(c.pct)}
                </button>
              ))}
            </div>
          )}
          <p className="text-[11px] text-muted-foreground/70 mt-3" data-testid="mb-footer">
            Sweep of {brief.brief_date} · {(brief.metrics_scoped != null ? brief.metrics_scoped : brief.metrics_evaluated) || 0} metric(s) scanned · {brief.anomalies_count || 0} anomaly(ies) · {brief.breaches_count || 0} breach(es) ({brief.sensitivity || sensitivity} sensitivity)
          </p>
        </>
      )}

      {/* Scheduling popup */}
      <Dialog open={schedOpen} onOpenChange={setSchedOpen}>
        <DialogContent className="max-w-md" data-testid="mb-schedule-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-sm"><CalendarClock className="w-4 h-4 text-primary" /> Morning Brief schedule & delivery</DialogTitle></DialogHeader>
          {cfg && (
            <div className="space-y-3">
              <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                <input type="checkbox" checked={!!cfg.enabled} onChange={e => patchCfg({ enabled: e.target.checked })} className="accent-primary w-4 h-4" data-testid="mb-sched-enabled" />
                Send a brief automatically every morning
              </label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground">Send time (local hour)</label>
                  <select value={cfg.send_hour ?? 7} onChange={e => patchCfg({ send_hour: Number(e.target.value) })} className={inputCls} data-testid="mb-sched-hour">
                    {Array.from({ length: 24 }).map((_, h) => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Timezone (IANA)</label>
                  <Input value={cfg.timezone || 'UTC'} onChange={e => patchCfg({ timezone: e.target.value })} placeholder="America/New_York" className={inputCls} data-testid="mb-sched-tz" />
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground mb-1.5">Delivery channels</div>
                <div className="flex flex-wrap gap-4">
                  {['inapp', 'email', 'slack', 'teams'].map(k => (
                    <label key={k} className="flex items-center gap-1.5 text-sm text-foreground cursor-pointer">
                      <input type="checkbox" checked={cfg.channels?.[k] ?? true} onChange={e => patchChannel(k, e.target.checked)} className="accent-primary w-4 h-4" data-testid={`mb-sched-channel-${k}`} />
                      {k === 'inapp' ? 'In-app' : k.charAt(0).toUpperCase() + k.slice(1)}
                    </label>
                  ))}
                </div>
              </div>
              {/* Quiet hours */}
              <div className="border-t border-border/40 pt-3 space-y-2">
                <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                  <input type="checkbox" checked={!!cfg.skip_weekends} onChange={e => patchCfg({ skip_weekends: e.target.checked })} className="accent-primary w-4 h-4" data-testid="mb-sched-skip-weekends" />
                  Only send on business days (skip weekends)
                </label>
                <div>
                  <label className="text-xs text-muted-foreground">Holidays to skip (YYYY-MM-DD, one per line)</label>
                  <textarea value={holidaysText} onChange={e => setHolidaysText(e.target.value)} rows={2} placeholder="2026-12-25&#10;2027-01-01" className={`${inputCls} font-mono text-xs`} data-testid="mb-sched-holidays" />
                </div>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Notify emails (comma / newline)</label>
                <Input value={emailsText} onChange={e => setEmailsText(e.target.value)} placeholder="cfo@acme.com" className={inputCls} data-testid="mb-sched-emails" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground">Slack channels</label>
                  <div className="mt-1"><SlackChannelPicker value={slackChannels} onChange={setSlackChannels} /></div>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Teams channels</label>
                  <div className="mt-1"><TeamsChannelPicker value={teamsChannels} onChange={setTeamsChannels} /></div>
                </div>
              </div>
              <Button onClick={saveSchedule} disabled={savingCfg} className="w-full" data-testid="mb-sched-save">
                {savingCfg ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null} Save schedule
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add to dashboard popup */}
      <Dialog open={dashOpen} onOpenChange={setDashOpen}>
        <DialogContent className="max-w-md" data-testid="mb-dash-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-sm"><LayoutDashboard className="w-4 h-4 text-primary" /> Add Morning Brief to a dashboard</DialogTitle></DialogHeader>
          <p className="text-[11px] text-muted-foreground -mt-1">Adds today's brief as a text tile to the dashboard you choose.</p>
          <button onClick={addToNew} disabled={!!addSaving} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-primary/40 text-primary hover:bg-primary/10 transition-colors text-left disabled:opacity-50" data-testid="mb-dash-new">
            {addSaving === 'new' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            <span className="text-xs font-medium">New dashboard…</span>
          </button>
          <div className="max-h-64 overflow-y-auto space-y-1.5 mt-1">
            {dashboards.length === 0 ? (
              <div className="text-center py-5 text-[11px] text-muted-foreground">No dashboards yet.</div>
            ) : dashboards.map(rep => (
              <button key={rep.report_id} onClick={() => addToExisting(rep.report_id)} disabled={!!addSaving} className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-muted/40 hover:bg-primary/10 border border-border/40 hover:border-primary/40 transition-colors text-left disabled:opacity-50" data-testid={`mb-dash-option-${rep.report_id}`}>
                <span className="text-xs font-medium text-foreground truncate">{rep.name}</span>
                {addSaving === rep.report_id ? <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" /> : <Plus className="w-3.5 h-3.5 text-primary" />}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
